import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import { Button } from "./ui/button";
import { Cookie, X } from "lucide-react";
import { useLocalizedHref } from "../hooks/useLocalizedSEO";

const STORAGE_KEY = "qrmp_consent_v1";

export function getConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function loadAnalytics() {
  const gaId = process.env.REACT_APP_GA_ID;
  if (!gaId || gaId === "G-XXXXXXXXXX") return;
  if (document.getElementById("ga4-script")) return;
  const s = document.createElement("script");
  s.id = "ga4-script";
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  window.gtag("config", gaId, { anonymize_ip: true });
}

export default function CookieConsent() {
  const { lang } = useLang();
  const [show, setShow] = useState(false);

  useEffect(() => {
    const consent = getConsent();
    if (!consent) {
      setShow(true);
    } else if (consent.analytics) {
      loadAnalytics();
    }
  }, []);

  const save = (analytics) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ necessary: true, analytics, ts: Date.now() }));
    if (analytics) loadAnalytics();
    setShow(false);
  };

  const lh = useLocalizedHref();

  if (!show) return null;

  const t = (k) => ({
    title: { de: "Cookies & Privatsphäre", en: "Cookies & Privacy", tr: "Çerezler ve Gizlilik" },
    body: {
      de: "Wir verwenden technisch notwendige Cookies, damit du dich anmelden und unseren Dienst nutzen kannst. Mit deiner Einwilligung verwenden wir zusätzlich Google Analytics, um unser Angebot zu verbessern. Du kannst deine Wahl jederzeit ändern.",
      en: "We use strictly necessary cookies to let you sign in and use the service. With your consent we also use Google Analytics to improve our service. You can change your choice at any time.",
      tr: "Oturum açabilmeniz ve hizmeti kullanabilmeniz için teknik olarak gerekli çerezler kullanıyoruz. İzninizle, hizmetimizi iyileştirmek için Google Analytics da kullanırız. Tercihinizi istediğiniz zaman değiştirebilirsiniz.",
    },
    acceptAll: { de: "Alle akzeptieren", en: "Accept all", tr: "Tümünü kabul et" },
    onlyNecessary: { de: "Nur notwendige", en: "Only necessary", tr: "Yalnızca gerekli" },
    learnMore: { de: "Datenschutz", en: "Privacy policy", tr: "Gizlilik" },
  })[k]?.[lang] || k;

  return (
    <div
      className="fixed inset-x-3 bottom-3 lg:left-auto lg:right-6 lg:bottom-6 lg:w-[420px] z-[60] rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 shadow-2xl p-5"
      data-testid="cookie-consent"
      role="dialog"
      aria-labelledby="cookie-title"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-purple-100 dark:bg-purple-500/10 grid place-items-center shrink-0">
          <Cookie className="h-4 w-4 text-purple-600 dark:text-purple-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 id="cookie-title" className="font-bold text-sm">{t("title")}</h3>
          <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400 leading-relaxed">
            {t("body")}{" "}
            <Link to={lh("/datenschutz")} className="text-purple-600 dark:text-purple-400 hover:underline">{t("learnMore")}</Link>
          </p>
        </div>
        <button
          aria-label="Dismiss"
          onClick={() => save(false)}
          className="text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
          data-testid="cookie-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          size="sm"
          className="rounded-full bg-purple-600 hover:bg-purple-700 text-white"
          onClick={() => save(true)}
          data-testid="cookie-accept-all"
        >
          {t("acceptAll")}
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="rounded-full"
          onClick={() => save(false)}
          data-testid="cookie-only-necessary"
        >
          {t("onlyNecessary")}
        </Button>
      </div>
    </div>
  );
}
